// src/BirthdaySearch.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiJSON } from "./apiBase";
import { useAuth } from "./context/AuthContext";
import MessageHistory, { newRetryKey, recordClientError, historyUserKey } from "./MessageHistory";
import ReplyTracker from "./ReplyTracker";
import Bv1FollowUp from "./Bv1FollowUp";

const BIRTHDAY_BRANCHES = ["jounieh", "tripoli", "batroun", "zgharta", "ehden"];

function localToday() {
    const value = new Date();
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

export function birthdayDateRange(from, to) {
    const valid = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) >= 1 && Number.isFinite(Date.parse(`${value}T00:00:00Z`))
        && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
    if (!valid(from) || !valid(to)) throw new Error("Choose valid From and To dates.");
    if (from > to) throw new Error("The To date must be on or after the From date.");
    const first = Date.parse(`${from}T00:00:00Z`), last = Date.parse(`${to}T00:00:00Z`);
    if ((last - first) / 86400000 + 1 > 366) throw new Error("Choose a date range of 366 days or fewer.");
    const days = [];
    for (let day = first; day <= last; day += 86400000) days.push(new Date(day).toISOString().slice(0, 10));
    return days;
}

export function birthdayPhone(value) {
    const first = (Array.isArray(value) ? value : [value])
        .flatMap((part) => String(part || "").split(/[,;|\/\r\n]+/)).map((part) => part.trim()).find(Boolean) || "";
    let phone = first.replace(/\D/g, "").replace(/^00/, "");
    if (phone.startsWith("9610")) phone = "961" + phone.slice(4);
    if (phone.startsWith("0") && phone.length >= 8) phone = "961" + phone.slice(1);
    if (!phone.startsWith("961") && [7, 8].includes(phone.length)) phone = "961" + phone;
    return phone;
}

export function birthdayBranchChoices(user) {
    if (!user) return [];
    const raw = user.branches || [];
    const claims = (Array.isArray(raw) ? raw : String(raw).replace(/;/g, ",").split(","))
        .map((value) => String(value).trim().toLowerCase());
    if (claims.includes("*")) return [...BIRTHDAY_BRANCHES];
    const allowed = BIRTHDAY_BRANCHES.filter((branch) => claims.includes(branch));
    const city = String(user.city || "").trim().toLowerCase();
    return claims.some(Boolean) ? allowed : BIRTHDAY_BRANCHES.includes(city) ? [city] : [];
}

export default function BirthdaySearch() {
    const auth = useAuth();
    const requestJSON = auth?.apiJSON || apiJSON;
    const userKey = historyUserKey(auth?.user);
    const userScope = userKey || String(auth?.user?.uid ?? "");
    const allowedBranches = useMemo(() => birthdayBranchChoices(auth?.user), [auth?.user]);
    const [branchSelection, setBranchSelection] = useState(() => ({
        owner: userScope, value: allowedBranches.length === 1 ? allowedBranches[0] : "",
    }));
    const manualBranch = branchSelection.owner === userScope && allowedBranches.includes(branchSelection.value)
        ? branchSelection.value : allowedBranches.length === 1 ? allowedBranches[0] : "";
    const activeBranch = userScope ? manualBranch : "";
    const currentScope = `${userScope}:${activeBranch}`;
    const scopeRef = useRef(currentScope);
    const sendLockRef = useRef("");
    const searchRevisionRef = useRef(0);
    scopeRef.current = currentScope;
    const [date, setDate] = useState(localToday);
    const [dateTo, setDateTo] = useState(localToday);
    const [messageMode, setMessageMode] = useState("greeting");
    const [workflow, setWorkflow] = useState("followup");
    const [followupBusy, setFollowupBusy] = useState(false);
    const [birthdayConfig, setBirthdayConfig] = useState({ configured: false, template: "", language: "en" });
    const [configError, setConfigError] = useState("");
    const [currentContact, setCurrentContact] = useState(null);
    const stopRef = useRef(false);
    const [results, setResults] = useState([]);
    const [duplicateCount, setDuplicateCount] = useState(0);
    const [voucherLinks, setVoucherLinks] = useState({});
    const [rowProgress, setRowProgress] = useState({});
    const [loading, setLoading] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);
    const [waBusy, setWaBusy] = useState(false);
    const [waReport, setWaReport] = useState(null);
    const [notice, setNotice] = useState("");
    const [unsavedError, setUnsavedError] = useState("");
    const [rangeFrom, setRangeFrom] = useState("");
    const [rangeTo, setRangeTo] = useState("");

    useEffect(() => {
        searchRevisionRef.current += 1;
        setWorkflow("followup");
        setFollowupBusy(false);
        setResults([]);
        setDuplicateCount(0);
        setVoucherLinks({});
        setRowProgress({});
        setWaReport(null);
        setNotice("");
        setUnsavedError("");
        setRangeFrom("");
        setRangeTo("");
        setLoading(false);
        setLinkLoading(false);
        setWaBusy(false);
        setCurrentContact(null);
        setBirthdayConfig({ configured: false, template: "", language: "en" });
        setConfigError("");
        let cancelled = false;
        if (activeBranch) requestJSON(`/wa360/birthday-config?branch=${encodeURIComponent(activeBranch)}`, {
            headers: { "x-branch": activeBranch },
        }).then((config) => { if (!cancelled) setBirthdayConfig(config); })
            .catch((error) => { if (!cancelled) setConfigError(error?.message || "Cannot check birthday greeting settings."); });
        return () => { cancelled = true; };
    }, [currentScope]);

    useEffect(() => () => { scopeRef.current = ""; }, []);

    const TEMPLATE_NAME = messageMode === "greeting" ? birthdayConfig.template : "bv_1";
    const LANG = messageMode === "greeting" ? birthdayConfig.language : "en";
    const C = {
        bg: "#0b1220",
        card: "#111827",
        card2: "#0f172a",
        border: "rgba(255,255,255,0.1)",
        text: "#e5e7eb",
        dim: "#94a3b8",
        green: "#10b981",
        red: "#ef4444",
        yellow: "#f59e0b",
        blue: "#2563eb",
    };

    const s = {
        wrap: {
            background: C.bg,
            color: C.text,
            minHeight: "100vh",
            padding: 16,
            fontFamily: "system-ui, Arial",
            maxWidth: 1400,
            margin: "0 auto",
        },
        card: {
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 14,
            padding: 14,
            marginBottom: 14,
        },
        input: {
            background: C.card2,
            border: `1px solid ${C.border}`,
            color: C.text,
            borderRadius: 10,
            padding: "10px 12px",
            minHeight: 42,
        },
        smallInput: {
            background: C.card2,
            border: `1px solid ${C.border}`,
            color: C.text,
            borderRadius: 10,
            padding: "10px 12px",
            minHeight: 42,
            width: 120,
        },
        btn: {
            background: "rgba(59,130,246,0.15)",
            border: "1px solid rgba(59,130,246,0.35)",
            color: C.text,
            borderRadius: 10,
            padding: "10px 12px",
            cursor: "pointer",
            minHeight: 42,
            fontWeight: 700,
        },
        btnGreen: {
            background: "rgba(16,185,129,0.18)",
            border: "1px solid rgba(16,185,129,0.4)",
            color: C.text,
            borderRadius: 10,
            padding: "10px 12px",
            cursor: "pointer",
            minHeight: 42,
            fontWeight: 800,
        },
        row: {
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
        },
        tableWrap: {
            overflowX: "auto",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            background: "#ffffff",
            maxHeight: "70vh",
        },
        table: {
            width: "100%",
            minWidth: 1150,
            borderCollapse: "collapse",
            color: "#000000",
            background: "#ffffff",
        },
        th: {
            background: "#f3f4f6",
            color: "#000000",
            textAlign: "left",
            padding: 10,
            borderBottom: "1px solid #d1d5db",
            position: "sticky",
            top: 0,
            zIndex: 2,
            fontWeight: 800,
            whiteSpace: "nowrap",
        },
        td: {
            padding: 10,
            borderBottom: "1px solid #e5e7eb",
            verticalAlign: "top",
            color: "#000000",
            background: "#ffffff",
        },
        tdAlt: {
            padding: 10,
            borderBottom: "1px solid #e5e7eb",
            verticalAlign: "top",
            color: "#000000",
            background: "#f9fafb",
        },
        selectedTd: {
            padding: 10,
            borderBottom: "1px solid #bfdbfe",
            verticalAlign: "top",
            color: "#000000",
            background: "#dbeafe",
        },
        linkText: {
            color: "#111827",
            wordBreak: "break-all",
            marginTop: 6,
            fontSize: 13,
        },
    };

    function digitsOnly(v) {
        return String(v || "").replace(/\D+/g, "");
    }

    function firstPhone(value) {
        // A contact may contain several numbers. Never concatenate them into an
        // invalid recipient; this page sends to the first listed number.
        return birthdayPhone(value);
    }

    function renderWhatsAppPhone(value) {
        const display = (Array.isArray(value) ? value : [value])
            .flatMap((part) => String(part || "").split(/[,;|\/\r\n]+/)).map((part) => part.trim()).find(Boolean) || "";
        const phone = birthdayPhone(display);

        if (!phone) return display || "-";

        return (
            <a
                href={`https://wa.me/${phone}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open WhatsApp chat with ${display}`}
                aria-label={`Open WhatsApp chat with ${display}`}
                style={{
                    color: "#047857",
                    fontWeight: 800,
                    textDecoration: "underline",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                }}
            >
                {display}
            </a>
        );
    }

    function cleanName(v) {
        return String(v || "").trim() || "Guest";
    }

    function safeFileName(v) {
        return (
            String(v || "UNKNOWN")
                .trim()
                .replace(/[<>:"/\\|?*]/g, "_") || "UNKNOWN"
        );
    }

    function linkKey(c) {
        const name = safeFileName(c?.name || c?.fullname || "");
        const phone = firstPhone(c?.phones || c?.phone || "");
        return `${c?._searchDate || date}|${name}|${phone}`;
    }

    function show(msg) {
        setNotice(msg);
        setTimeout(() => setNotice(""), 5000);
    }

    function setProgress(key, patch) {
        setRowProgress((m) => ({
            ...m,
            [key]: {
                ...(m[key] || {}),
                ...patch,
            },
        }));
    }

    async function safePostJSON(path, payload) {
        return requestJSON(path, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...(activeBranch ? { "x-branch": activeBranch } : {}),
            },
            body: JSON.stringify(payload),
        });
    }

    async function saveClientError(c, code, message, status = "invalid", requestKey = newRetryKey()) {
        const errorScope = scopeRef.current;
        try {
            await recordClientError(requestJSON, {
                source: "birthday", search_date: c?._searchDate || date, branch: activeBranch,
                customer_id: String(c?.id ?? c?._id ?? ""),
                customer_name: cleanName(c?.name || c?.fullname || ""),
                phone: firstPhone(c?.phones || c?.phone || ""),
                template_name: TEMPLATE_NAME, error_code: code,
                error_message: message, status, request_key: requestKey,
            }, userKey);
            return true;
        } catch (e) {
            if (scopeRef.current === errorScope) setUnsavedError(`${e?.message || e}. Keep this page open until you have saved the error details.`);
            return false;
        }
    }

    function getSelectedRows() {
        if (!results.length) return [];

        let from = parseInt(rangeFrom || "1", 10);
        let to = parseInt(rangeTo || String(results.length), 10);

        if (Number.isNaN(from) || from < 1) from = 1;
        if (Number.isNaN(to) || to > results.length) to = results.length;

        if (from > to) return [];

        return results.slice(from - 1, to);
    }

    function isRowSelected(index) {
        let from = parseInt(rangeFrom || "1", 10);
        let to = parseInt(rangeTo || String(results.length), 10);

        if (Number.isNaN(from) || from < 1) from = 1;
        if (Number.isNaN(to) || to > results.length) to = results.length;

        const rowNumber = index + 1;
        return rowNumber >= from && rowNumber <= to;
    }

    const selectedRows = useMemo(
        () => getSelectedRows(),
        [results, rangeFrom, rangeTo]
    );

    async function handleSearch() {
        if (!activeBranch) {
            show("Select your branch first.");
            return;
        }
        let days;
        try { days = birthdayDateRange(date, dateTo); }
        catch (error) { show(error.message); return; }

        const searchScope = scopeRef.current;
        const searchRevision = ++searchRevisionRef.current;
        setLoading(true);
        setResults([]);
        setDuplicateCount(0);
        setVoucherLinks({});
        setRowProgress({});
        setWaReport(null);
        setRangeFrom("");
        setRangeTo("");

        try {
            const contacts = new Map();
            let duplicates = 0;
            for (const day of days) {
                const data = await requestJSON(`/birthdays?date=${encodeURIComponent(day)}`, {
                    headers: activeBranch ? { "x-branch": activeBranch } : {},
                });
                if (scopeRef.current !== searchScope || searchRevisionRef.current !== searchRevision) return;
                const rows = Array.isArray(data) ? data : data?.contacts || [];
                rows.forEach((row) => {
                    const phone = firstPhone(row?.phones || row?.phone);
                    // One greeting per full international number across this search.
                    // The earliest matching day wins, including a year boundary.
                    const key = /^[1-9]\d{9,14}$/.test(phone) ? `phone:${phone}`
                        : `${day}|${row?.source_db || row?.db || ""}|${row?.id ?? row?._id ?? ""}|${cleanName(row?.name || row?.fullname)}|${phone}`;
                    if (contacts.has(key)) duplicates += 1;
                    else contacts.set(key, { ...row, _searchDate: day });
                });
                setNotice(`Searching ${day} · ${contacts.size} contacts found`);
            }
            setResults([...contacts.values()]);
            setDuplicateCount(duplicates);
            show(`Loaded ${contacts.size} contacts from ${date} to ${dateTo}.${duplicates ? ` Excluded ${duplicates} duplicate phone entries; the earliest matching birthday date is kept.` : ""}`);
        } catch (e) {
            if (scopeRef.current !== searchScope || searchRevisionRef.current !== searchRevision) return;
            console.error(e);
            show(`Failed to load birthdays: ${e?.message || e}`);
        } finally {
            if (scopeRef.current === searchScope && searchRevisionRef.current === searchRevision) setLoading(false);
        }
    }

    async function fetchVoucherLinkFor(c) {
        const voucherScope = scopeRef.current;
        const name = safeFileName(c?.name || c?.fullname || "");
        const phone = firstPhone(c?.phones || c?.phone || "");
        const key = linkKey(c);

        if (voucherLinks[key] && !voucherLinks[key].error) return voucherLinks[key];

        try {
            setProgress(key, {
                status: "GETTING_VOUCHER",
                message: "Getting voucher link...",
            });

            const qs = new URLSearchParams({ date: c?._searchDate || date, name, phone });
            const data = await requestJSON(`/voucher/link?${qs.toString()}`, {
                headers: activeBranch ? { "x-branch": activeBranch } : {},
            });
            if (scopeRef.current !== voucherScope) return { error: "The branch or signed-in user changed." };

            if (!data?.view_url) throw new Error("The voucher service did not return a voucher image.");

            const out = {
                page_url: data?.page_url || "",
                view_url: data?.view_url || "",
            };

            setVoucherLinks((m) => ({ ...m, [key]: out }));
            setProgress(key, { status: "VOUCHER_READY", message: "Voucher ready" });

            return out;
        } catch (e) {
            if (scopeRef.current !== voucherScope) return { error: "The branch or signed-in user changed." };
            const out = {
                error: e?.message || String(e),
                page_url: "",
                view_url: "",
            };

            setVoucherLinks((m) => ({ ...m, [key]: out }));
            setProgress(key, { status: "VOUCHER_FAILED", message: out.error });

            return out;
        }
    }

    async function getVoucherLinksForRows(rows) {
        if (!activeBranch) {
            show("Select your branch first.");
            return {};
        }
        if (!rows.length) {
            show("No contacts selected.");
            return {};
        }

        const linkScope = scopeRef.current;
        setLinkLoading(true);
        const finalLinks = {};

        try {
            for (const c of rows) {
                if (scopeRef.current !== linkScope) break;
                const key = linkKey(c);
                const link = await fetchVoucherLinkFor(c);
                finalLinks[key] = link || {};
            }

            if (scopeRef.current === linkScope) show(`Voucher links requested for ${rows.length} selected contacts.`);
            return finalLinks;
        } finally {
            if (scopeRef.current === linkScope) setLinkLoading(false);
        }
    }

    function buildWaPayload({ to, name, image, activeBranch, customerId, requestId, searchDate }) {
        return {
            source: "birthday",
            search_date: searchDate,
            campaign_name: "birthday",
            request_id: requestId,
            template: TEMPLATE_NAME,
            template_name: TEMPLATE_NAME,
            lang: LANG,
            language: LANG,
            requested_limit: 1,
            limit: 1,
            filter_emoji_names: false,
            filter_wrong_number_names: false,
            branch: activeBranch || "",
            items: [
                {
                    to,
                    phone: to,
                    name,
                    customer_id: String(customerId ?? ""),
                    image,
                    image_url: image,
                    header_image: image,
                    branch: activeBranch || "",
                },
            ],
        };
    }

    async function sendSelectedContactsWithVoucherLinks() {
        if (sendLockRef.current === scopeRef.current) return;
        if (messageMode === "greeting" && !birthdayConfig.configured) {
            show("Configure your approved birthday greeting template before sending.");
            return;
        }
        if (!activeBranch) {
            show("Select your branch before sending.");
            return;
        }
        if (!date) {
            show("Pick a date first.");
            return;
        }

        if (!results.length) {
            show("No contacts loaded.");
            return;
        }

        const rowsToSend = getSelectedRows();

        if (!rowsToSend.length) {
            show("No contacts selected. Check From / To numbers.");
            return;
        }

        const sendScope = scopeRef.current;
        sendLockRef.current = sendScope;
        stopRef.current = false;
        setWaBusy(true);
        setWaReport(null);

        const reportRows = [];
        let sent = 0;
        let failed = 0;
        let notWhatsapp = 0;
        let skippedInvalid = 0;

        try {
            rowsToSend.forEach((c) => {
                const key = linkKey(c);
                setProgress(key, {
                    status: "WAITING",
                    message: "Waiting...",
                    wamid: "",
                    wa_error_code: "",
                });
            });

            show(`Sending ${rowsToSend.length} selected contacts one by one...`);

            for (let i = 0; i < rowsToSend.length; i++) {
                if (scopeRef.current !== sendScope) return;
                if (stopRef.current) break;
                const c = rowsToSend[i];
                const key = linkKey(c);

                const to = firstPhone(c?.phones || c?.phone || "");
                const name = cleanName(c?.name || c?.fullname || "");
                setCurrentContact({ name, to, date: c?._searchDate || date, index: i + 1, total: rowsToSend.length });

                if (!/^[1-9]\d{9,14}$/.test(to)) {
                    skippedInvalid += 1;

                    const row = {
                        to,
                        name,
                        status: "SKIPPED_INVALID",
                        wa_error_code: "NO_PHONE",
                        wamid: "",
                    };

                    reportRows.push(row);

                    setProgress(key, {
                        status: "SKIPPED_INVALID",
                        message: "Check the full international phone number.",
                        wa_error_code: "NO_PHONE",
                    });

                    await saveClientError(c, "INVALID_PHONE", "Check the full international phone number.");

                    continue;
                }

                let imageToSend = "";
                if (messageMode === "voucher") {
                    setProgress(key, { status: "GETTING_VOUCHER", message: `Getting voucher ${i + 1}/${rowsToSend.length}` });
                    const link = await fetchVoucherLinkFor(c);
                    if (scopeRef.current !== sendScope) return;
                    if (stopRef.current) break;
                    if (link?.error || !link?.view_url) {
                        failed += 1;
                        const message = link?.error || "The voucher image is unavailable.";
                        reportRows.push({ to, name, status: "VOUCHER_FAILED", wa_error_code: message, wamid: "" });
                        setProgress(key, { status: "VOUCHER_FAILED", message, wa_error_code: message });
                        await saveClientError(c, "VOUCHER_FAILED", message, "failed");
                        setWaReport({ total_considered: rowsToSend.length, sent, failed, not_whatsapp: notWhatsapp, skipped_invalid: skippedInvalid, results: [...reportRows] });
                        continue;
                    }
                    imageToSend = link.view_url;
                }

                setProgress(key, {
                    status: "SENDING",
                    message: `Sending ${i + 1}/${rowsToSend.length}`,
                });

                const requestId = newRetryKey();
                try {
                    const payload = buildWaPayload({
                        to,
                        name,
                        image: imageToSend,
                        activeBranch,
                        customerId: c?.id ?? c?._id,
                        requestId,
                        searchDate: c?._searchDate || date,
                    });

                    const res = messageMode === "greeting"
                        ? await safePostJSON("/wa360/send-birthday-greeting", {
                            to, name, branch: activeBranch, search_date: c?._searchDate || date,
                            customer_id: String(c?.id ?? c?._id ?? ""), request_id: requestId,
                        }) : await safePostJSON("/wa360/send-batch", payload);
                    if (scopeRef.current !== sendScope) return;

                    const first = messageMode === "greeting" ? res : Array.isArray(res?.results) ? res.results[0] : null;

                    const status = String(
                        first?.wa_status || first?.status ||
                        first?.result ||
                        (res?.sent || res?.success ? "ACCEPTED" : "FAILED")
                    ).toUpperCase();

                    if (["ACCEPTED", "SENT", "DELIVERED", "READ"].includes(status) && !first?.dispatch_blocked) sent += 1;
                    else if (["NOT_WHATSAPP", "UNDELIVERABLE"].includes(status)) notWhatsapp += 1;
                    else if (!first?.dispatch_blocked) failed += 1;

                    const row = {
                        to,
                        name,
                        status,
                        blocked: Boolean(first?.dispatch_blocked),
                        wa_error_code:
                            first?.wa_error_code ||
                            first?.error_code ||
                            first?.error_message ||
                            first?.error ||
                            res?.error ||
                            "",
                        wamid:
                            first?.wamid ||
                            first?.message_id ||
                            first?.id ||
                            res?.wamid ||
                            "",
                    };

                    reportRows.push(row);

                    setProgress(key, {
                        status,
                        message:
                            first?.dispatch_blocked ? first?.next_step || first?.reason || "Already processed or blocked. See saved history."
                                : ["ACCEPTED", "SENT", "DELIVERED", "READ"].includes(status)
                                ? "Accepted; see saved history for delivery"
                                : ["NOT_WHATSAPP", "UNDELIVERABLE"].includes(status)
                                    ? "Undeliverable"
                                    : "Failed",
                        wa_error_code: row.wa_error_code,
                        wamid: row.wamid,
                    });

                    setWaReport({
                        total_considered: rowsToSend.length,
                        sent,
                        failed,
                        not_whatsapp: notWhatsapp,
                        skipped_invalid: skippedInvalid,
                        results: [...reportRows],
                    });
                    if (status === "UNKNOWN") {
                        stopRef.current = true;
                        setNotice("Sending paused because the result is uncertain. Check saved history before sending again. Remaining contacts have not been attempted.");
                        break;
                    }
                } catch (e) {
                    if (scopeRef.current !== sendScope) return;
                    failed += 1;

                    const row = {
                        to,
                        name,
                        status: "UNKNOWN",
                        wa_error_code: e?.message || String(e),
                        wamid: "",
                        error: e?.message || String(e),
                    };

                    reportRows.push(row);

                    setProgress(key, {
                        status: "UNKNOWN",
                        message: "Delivery is unknown. Check saved history before resending.",
                        wa_error_code: e?.message || String(e),
                    });

                    await saveClientError(c, "CLIENT_TRANSPORT_ERROR", `${e?.message || e}. Check saved history before resending.`, "unknown", requestId);

                    setWaReport({
                        total_considered: rowsToSend.length,
                        sent,
                        failed,
                        not_whatsapp: notWhatsapp,
                        skipped_invalid: skippedInvalid,
                        results: [...reportRows],
                    });
                    stopRef.current = true;
                    setNotice("Sending paused after a connection error. The remaining contacts have not been attempted. Refresh the reply report and saved history before continuing.");
                    break;
                }
            }

            if (!stopRef.current) show(
                `Done. Accepted: ${sent}, Failed or unknown: ${failed}, Undeliverable: ${notWhatsapp}, Skipped: ${skippedInvalid}. See saved history for delivery status.`
            );
        } finally {
            if (sendLockRef.current === sendScope) sendLockRef.current = "";
            if (scopeRef.current === sendScope) setWaBusy(false);
        }
    }

    function exportFile(type) {
        if (!results.length) return;
        const phoneValues = (c) => (Array.isArray(c?.phones || c?.phone) ? c.phones || c.phone : [c?.phones || c?.phone])
            .flatMap((value) => String(value || "").split(/[,;|\/\r\n]+/)).map((value) => value.trim()).filter(Boolean);
        const csv = (value) => {
            const text = String(value ?? "");
            const safe = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
            return `"${safe.replace(/"/g, '""')}"`;
        };
        const vcf = (value) => String(value || "").replace(/\\/g, "\\\\").replace(/\r?\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
        const content = type === "csv" ? "\uFEFF" + [["Name", "Phone", "Birthday", "Matched date"], ...results.map((c) => [cleanName(c?.name || c?.fullname), phoneValues(c).join(", "), c?.birthday || "", c._searchDate])].map((row) => row.map(csv).join(",")).join("\r\n")
            : results.map((c) => ["BEGIN:VCARD", "VERSION:3.0", `FN:${vcf(cleanName(c?.name || c?.fullname))}`, ...phoneValues(c).map((value) => `TEL;TYPE=CELL:${vcf(value)}`), `NOTE:Birthday search ${c._searchDate}`, "END:VCARD"].join("\r\n")).join("\r\n");
        const url = URL.createObjectURL(new Blob([content], { type: type === "csv" ? "text/csv;charset=utf-8" : "text/vcard;charset=utf-8" }));
        const anchor = document.createElement("a");
        anchor.href = url; anchor.download = `birthdays_${activeBranch}_${date}_${dateTo}.${type}`; anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    function selectAll() {
        setRangeFrom("1");
        setRangeTo(String(results.length));
    }

    function selectFromCurrentToEnd() {
        if (!rangeFrom) {
            show("Enter From number first, example 251.");
            return;
        }

        setRangeTo("");
    }

    function statusStyle(status) {
        if (status === "ACCEPTED") return { color: "#047857", label: "Accepted by provider" };
        if (status === "UNKNOWN") return { color: "#b45309", label: "Unknown — check saved history" };
        if (status === "UNDELIVERABLE") return { color: "#b45309", label: "Undeliverable" };
        if (status === "SENT") return { color: "#047857", label: "Sent" };
        if (status === "SENDING")
            return { color: "#2563eb", label: "Sending..." };
        if (status === "GETTING_VOUCHER")
            return { color: "#2563eb", label: "Getting voucher..." };
        if (status === "VOUCHER_READY")
            return { color: "#047857", label: "Voucher ready" };
        if (status === "NOT_WHATSAPP")
            return { color: "#b45309", label: "Not WhatsApp" };
        if (status === "SKIPPED_INVALID")
            return { color: "#b45309", label: "Skipped" };
        if (status === "FAILED" || status === "VOUCHER_FAILED")
            return { color: "#b91c1c", label: "Failed" };
        if (status === "WAITING") return { color: "#6b7280", label: "Waiting" };
        return { color: "#6b7280", label: "-" };
    }

    const summary = useMemo(() => {
        if (!waReport) return null;

        return {
            total: Number(waReport.total_considered || 0),
            sent: Number(waReport.sent || 0),
            failed: Number(waReport.failed || 0),
            notWhatsapp: Number(waReport.not_whatsapp || 0),
            skippedInvalid: Number(waReport.skipped_invalid || 0),
        };
    }, [waReport]);

    return (
        <div className="birthday-page" style={s.wrap}>
            <style>{`.birthday-page button:disabled{opacity:.5;cursor:not-allowed}.birthday-page button:focus-visible,.birthday-page input:focus-visible,.birthday-page select:focus-visible{outline:3px solid #63b3ff;outline-offset:3px}.birthday-page input[type=date]{color-scheme:dark}@media(max-width:640px){.birthday-page{padding:10px!important}.birthday-page .birthday-date-controls>label{flex:1 1 140px}.birthday-page .birthday-date-controls>button{flex:1 1 140px}}`}</style>
            <div style={s.card}>
                <h2 style={{ margin: 0, marginBottom: 6 }}>
                    🎂 Birthday follow-ups
                </h2>

                <div style={{ color: C.dim }}>
                    Send Happy birthday 🎂 only to guests who haven’t replied after their first bv_1 message.
                </div>
                <label style={{ display: "block", marginTop: 14 }}>
                    <span style={{ display: "block", color: C.dim, marginBottom: 6 }}>Branch</span>
                    <select
                        aria-label="Birthday branch"
                        value={activeBranch}
                        disabled={!userScope || !allowedBranches.length || loading || waBusy || linkLoading || followupBusy}
                        onChange={(event) => setBranchSelection({ owner: userScope, value: event.target.value })}
                        style={{ ...s.input, minWidth: 190 }}
                    >
                        <option value="">Choose branch</option>
                        {allowedBranches.map((branch) => <option key={branch} value={branch}>{branch[0].toUpperCase() + branch.slice(1)}</option>)}
                    </select>
                </label>
                {!allowedBranches.length && <div role="status" style={{ marginTop: 8, color: C.yellow }}>Your signed-in account has no assigned branch. Ask an administrator to update your branch access.</div>}
                <div style={{ ...s.row, marginTop: 16 }}>
                    <button type="button" aria-pressed={workflow === "followup"} style={workflow === "followup" ? s.btnGreen : s.btn} disabled={waBusy || linkLoading || followupBusy} onClick={() => setWorkflow("followup")}>Follow up non-repliers</button>
                    <button type="button" aria-pressed={workflow === "other"} style={s.btn} disabled={waBusy || linkLoading || followupBusy} onClick={() => setWorkflow("other")}>Other birthday tools · all guests</button>
                </div>
            </div>

            {workflow === "followup" && <>
                <Bv1FollowUp branch={activeBranch} userKey={userKey} apiJSON={requestJSON} defaultFrom={date} defaultTo={dateTo} onBusyChange={setFollowupBusy} />
                <details style={s.card}><summary style={{ cursor: "pointer", fontWeight: 700 }}>Saved sending history &amp; error details</summary><MessageHistory branch={activeBranch} source="birthday" userKey={userKey} apiJSON={requestJSON} refreshToken={followupBusy} /></details>
            </>}

            {workflow === "other" && <>
            <p style={{ color: C.yellow }}>These original tools can send to all selected birthday guests. Use “Follow up non-repliers” for the first bv_1 reply check.</p>

            {notice && (
                <div role="status" aria-live="polite" style={{ ...s.card, background: "rgba(59,130,246,0.12)" }}>
                    <b>{notice}</b>
                </div>
            )}
            {unsavedError && <div role="alert" style={{ ...s.card, color: "#fecaca", whiteSpace: "pre-wrap" }}>{unsavedError}</div>}

            <div style={s.card}>
                <h3 style={{ marginTop: 0 }}>1. Find birthdays</h3>
                <div className="birthday-date-controls" style={s.row}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>From date<input
                        aria-label="Birthday from date"
                        type="date"
                        value={date}
                        disabled={loading || waBusy || linkLoading}
                        onChange={(e) => {
                            setDate(e.target.value);
                            setResults([]);
                            setDuplicateCount(0);
                            searchRevisionRef.current += 1;
                            setVoucherLinks({});
                            setRowProgress({});
                            setWaReport(null);
                            setRangeFrom("");
                            setRangeTo("");
                            setCurrentContact(null);
                        }}
                        style={s.input}
                    /></label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>To date<input
                        aria-label="Birthday to date" type="date" value={dateTo}
                        disabled={loading || waBusy || linkLoading}
                        onChange={(event) => {
                            setDateTo(event.target.value); setResults([]); setDuplicateCount(0); searchRevisionRef.current += 1; setVoucherLinks({}); setRowProgress({}); setWaReport(null); setRangeFrom(""); setRangeTo(""); setCurrentContact(null);
                        }} style={s.input}
                    /></label>

                    <button
                        style={s.btn}
                        onClick={handleSearch}
                        disabled={!activeBranch || loading || waBusy || linkLoading}
                    >
                        {loading ? "Searching..." : "Find birthdays"}
                    </button>

                    <button
                        style={s.btn}
                        onClick={() => exportFile("csv")}
                        disabled={!results.length}
                    >
                        Export CSV
                    </button>

                    <button
                        style={s.btn}
                        onClick={() => exportFile("vcf")}
                        disabled={!results.length}
                    >
                        Export VCF
                    </button>
                </div>
                <div style={{ color: C.dim, marginTop: 10 }}>Both dates are included. Use the same date for one day. Search up to 366 days.</div>
            </div>

            <div style={s.card}>
                <h3 style={{ marginTop: 0 }}>2. Choose message & contacts</h3>
                <label style={{ display: "block", marginBottom: 12 }}>Message option<br /><select aria-label="Birthday message option" value={messageMode} disabled={loading || waBusy || linkLoading} onChange={(event) => setMessageMode(event.target.value)} style={{ ...s.input, marginTop: 6, maxWidth: "100%" }}>
                    <option value="greeting">Birthday greeting · text and cake emoji</option>
                    <option value="voucher">Existing birthday voucher</option>
                </select></label>
                <div style={{ border: "1px solid #23645f", borderRadius: 12, padding: 16, background: "#0a2930", marginBottom: 14 }}>
                    <b style={{ fontSize: 21 }}>{messageMode === "greeting" ? "Happy birthday 🎂" : "Birthday voucher"}</b>
                    <div style={{ color: C.dim, marginTop: 6 }}>{messageMode === "greeting" ? "Text only, using your approved birthday greeting template." : "Your existing voucher image and approved bv_1 message."}</div>
                </div>
                {messageMode === "greeting" && !birthdayConfig.configured && <p role="status" style={{ color: "#ffda95", lineHeight: 1.6 }}>{configError || "Birthday greeting setup is required. Ask your administrator to connect the approved text-only Happy birthday 🎂 template, then reload this page."}</p>}

                <div style={s.row}>
                    <div>
                        <label htmlFor="birthday-first-contact" style={{ display: "block", marginBottom: 6, color: C.dim, fontSize: 13 }}>First contact number</label>
                        <input
                            id="birthday-first-contact"
                            type="number"
                            min="1"
                            placeholder="251"
                            value={rangeFrom}
                            disabled={loading || waBusy || linkLoading}
                            onChange={(e) => setRangeFrom(e.target.value)}
                            style={s.smallInput}
                        />
                    </div>

                    <div>
                        <label htmlFor="birthday-last-contact" style={{ display: "block", marginBottom: 6, color: C.dim, fontSize: 13 }}>Last contact number</label>
                        <input
                            id="birthday-last-contact"
                            type="number"
                            min="1"
                            placeholder="Empty = end"
                            value={rangeTo}
                            disabled={loading || waBusy || linkLoading}
                            onChange={(e) => setRangeTo(e.target.value)}
                            style={s.smallInput}
                        />
                    </div>

                    <button
                        style={s.btn}
                        onClick={selectAll}
                        disabled={!results.length || waBusy || linkLoading}
                    >
                        Select All
                    </button>

                    <button
                        style={s.btn}
                        onClick={selectFromCurrentToEnd}
                        disabled={!results.length || waBusy || linkLoading}
                    >
                        From first number to end
                    </button>

                    {messageMode === "voucher" && <button
                        style={s.btn}
                        onClick={() => getVoucherLinksForRows(selectedRows)}
                        disabled={!selectedRows.length || waBusy || linkLoading}
                    >
                        {linkLoading ? "Getting Links..." : `Get Links (${selectedRows.length})`}
                    </button>}

                    <button
                        style={s.btnGreen}
                        onClick={sendSelectedContactsWithVoucherLinks}
                        disabled={!activeBranch || !selectedRows.length || loading || waBusy || linkLoading || (messageMode === "greeting" && !birthdayConfig.configured)}
                    >
                        {waBusy ? "Sending..." : `Send to ${selectedRows.length} contacts`}
                    </button>
                    {waBusy && <button style={s.btn} onClick={() => { stopRef.current = true; setNotice("Stop requested. The current request may finish; no further contact will be attempted."); }}>Stop after current contact</button>}
                </div>

                <div style={{ marginTop: 12, color: C.dim }}>
                    Example: From <b style={{ color: C.text }}>251</b>, To empty = send
                    from 251 till the end.
                </div>
                {currentContact && <p role="status">{waBusy ? "Current contact" : "Last contact in this session"}: <b>{currentContact.name}</b> · {currentContact.to} · {currentContact.date} · {currentContact.index} of {currentContact.total}</p>}
            </div>

            <ReplyTracker branch={activeBranch} source="birthday" userKey={userKey} apiJSON={requestJSON} refreshToken={`${waBusy}-${waReport?.results?.length || 0}`} defaultFrom={date} defaultTo={dateTo} />

            <details style={s.card}><summary style={{ cursor: "pointer", fontWeight: 700 }}>Saved sending history & error details</summary><p style={{ color: C.dim }}>Every attempt stays saved after refresh or connection loss. Check uncertain results before sending again.</p><MessageHistory branch={activeBranch} source="birthday" userKey={userKey} apiJSON={requestJSON} refreshToken={`${waBusy}-${waReport?.results?.length || 0}`} /></details>

            <div style={s.card}>
                <div style={s.row}>
                    <div>
                        <div style={{ color: C.dim, fontSize: 13 }}>Birthday dates</div>
                        <b>{date || "-"} → {dateTo || "-"}</b>
                    </div>

                    <div>
                        <div style={{ color: C.dim, fontSize: 13 }}>Loaded Contacts</div>
                        <b>{results.length}</b>
                    </div>
                    {duplicateCount > 0 && <div><div style={{ color: C.dim, fontSize: 13 }}>Duplicate phone entries excluded</div><b>{duplicateCount}</b></div>}

                    <div>
                        <div style={{ color: C.dim, fontSize: 13 }}>Selected To Send</div>
                        <b style={{ color: C.green }}>{selectedRows.length}</b>
                    </div>

                    <div>
                        <div style={{ color: C.dim, fontSize: 13 }}>Template</div>
                        <b>{TEMPLATE_NAME || "Setup required"}</b>
                    </div>

                    {summary && (
                        <>
                            <div>
                                <div style={{ color: C.dim, fontSize: 13 }}>Accepted</div>
                                <b style={{ color: C.green }}>{summary.sent}</b>
                            </div>

                            <div>
                                <div style={{ color: C.dim, fontSize: 13 }}>Failed</div>
                                <b style={{ color: C.red }}>{summary.failed}</b>
                            </div>

                            <div>
                                <div style={{ color: C.dim, fontSize: 13 }}>Undeliverable</div>
                                <b style={{ color: C.yellow }}>{summary.notWhatsapp}</b>
                            </div>

                            <div>
                                <div style={{ color: C.dim, fontSize: 13 }}>Skipped</div>
                                <b style={{ color: C.yellow }}>{summary.skippedInvalid}</b>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={s.card}>
                <h3 style={{ marginTop: 0 }}>Birthday Contact List</h3>

                {!loading && results.length === 0 && (
                    <div style={{ color: C.dim }}>No contacts loaded.</div>
                )}

                {results.length > 0 && (
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>#</th>
                                    <th style={s.th}>Selected</th>
                                    <th style={s.th}>Progress</th>
                                    <th style={s.th}>Name</th>
                                    <th style={s.th}>Phone</th>
                                    <th style={s.th}>Birthday</th>
                                    {messageMode === "voucher" && <th style={s.th}>Voucher Link</th>}
                                    {messageMode === "voucher" && <th style={s.th}>Header Image Used</th>}
                                </tr>
                            </thead>

                            <tbody>
                                {results.map((c, i) => {
                                    const key = linkKey(c);
                                    const link = voucherLinks[key];
                                    const progress = rowProgress[key] || {};
                                    const st = statusStyle(progress.status);
                                    const selected = isRowSelected(i);
                                    const cellStyle = selected
                                        ? s.selectedTd
                                        : i % 2 === 0
                                            ? s.td
                                            : s.tdAlt;

                                    return (
                                        <tr key={i}>
                                            <td style={{ ...cellStyle, fontWeight: 800 }}>
                                                {i + 1}
                                            </td>

                                            <td style={cellStyle}>
                                                {selected ? (
                                                    <b style={{ color: "#047857" }}>YES</b>
                                                ) : (
                                                    <span style={{ color: "#6b7280" }}>NO</span>
                                                )}
                                            </td>

                                            <td style={cellStyle}>
                                                <b style={{ color: st.color }}>{st.label}</b>

                                                {progress.message && (
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            color: "#374151",
                                                            marginTop: 4,
                                                            maxWidth: 260,
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {progress.message}
                                                    </div>
                                                )}

                                                {progress.wa_error_code && (
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            color: "#991b1b",
                                                            marginTop: 4,
                                                            maxWidth: 260,
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        Error: {progress.wa_error_code}
                                                    </div>
                                                )}
                                                {(progress.status === "VOUCHER_FAILED" || link?.error) && (
                                                    <button
                                                        type="button"
                                                        style={{ ...s.btn, color: "#ffffff", background: C.blue, marginTop: 6 }}
                                                        disabled={waBusy || linkLoading}
                                                        onClick={() => getVoucherLinksForRows([c])}
                                                    >
                                                        Retry voucher
                                                    </button>
                                                )}
                                            </td>

                                            <td style={cellStyle}>{c?.name || c?.fullname || "-"}</td>

                                            <td style={cellStyle}>
                                                {renderWhatsAppPhone(c?.phones || c?.phone)}
                                            </td>

                                            <td style={cellStyle}>{c?._searchDate || "-"}<div style={{ fontSize: 12, color: "#64748b", marginTop: 5 }}>{c?.birthday || ""}</div></td>

                                            {messageMode === "voucher" && <td style={cellStyle}>
                                                {link?.error ? (
                                                    <span style={{ color: "#b91c1c", fontWeight: 700 }}>
                                                        {link.error}
                                                    </span>
                                                ) : link?.page_url ? (
                                                    <div>
                                                        <button
                                                            style={{
                                                                ...s.btn,
                                                                color: "#ffffff",
                                                                background: C.blue,
                                                            }}
                                                            onClick={() => window.open(link.page_url, "_blank")}
                                                        >
                                                            Open
                                                        </button>
                                                        <div style={s.linkText}>{link.page_url}</div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        style={{
                                                            ...s.btn,
                                                            color: "#ffffff",
                                                            background: C.blue,
                                                        }}
                                                        onClick={() => fetchVoucherLinkFor(c)}
                                                        disabled={waBusy}
                                                    >
                                                        Get Link
                                                    </button>
                                                )}
                                            </td>}

                                            {messageMode === "voucher" && <td style={cellStyle}>
                                                <div style={s.linkText}>
                                                    {link?.view_url || "Voucher not generated"}
                                                </div>
                                            </td>}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {waReport?.results?.length > 0 && (
                <div style={s.card}>
                    <h3 style={{ marginTop: 0 }}>Send Report</h3>

                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>#</th>
                                    <th style={s.th}>To</th>
                                    <th style={s.th}>Name</th>
                                    <th style={s.th}>Status</th>
                                    <th style={s.th}>Error Code</th>
                                    <th style={s.th}>WAMID</th>
                                </tr>
                            </thead>

                            <tbody>
                                {waReport.results.map((r, i) => {
                                    const cellStyle = i % 2 === 0 ? s.td : s.tdAlt;

                                    return (
                                        <tr key={i}>
                                            <td style={{ ...cellStyle, fontWeight: 800 }}>
                                                {i + 1}
                                            </td>

                                            <td style={cellStyle}>
                                                {renderWhatsAppPhone(r.to)}
                                            </td>

                                            <td style={cellStyle}>{r.name || "-"}</td>

                                            <td style={cellStyle}>
                                                <b
                                                    style={{
                                                        color:
                                                            ["ACCEPTED", "SENT", "DELIVERED", "READ"].includes(r.status)
                                                                ? "#047857"
                                                                : ["NOT_WHATSAPP", "UNDELIVERABLE", "UNKNOWN"].includes(r.status)
                                                                    ? "#b45309"
                                                                    : "#b91c1c",
                                                    }}
                                                >
                                                    {r.status || "-"}
                                                </b>
                                            </td>

                                            <td style={cellStyle}>{r.wa_error_code || "-"}</td>

                                            <td style={cellStyle}>
                                                {r.wamid ? `${String(r.wamid).slice(0, 28)}...` : "-"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            </>}
        </div>
    );
}
